import React, { useEffect, useMemo, useRef, useState } from "react";
import sha256 from "crypto-js/sha256";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { queryEmployee, changePassword, getSuspensionRemarks } from "./model";
import { isAllInputValid } from "utils/formValidation";
import s from "./OfflineEmployees.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import ColumnFilter from "components/ColumnFilter/ColumnFilter";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import ButtonMAS from "ButtonMAS/ButtonMAS";

import merchantManagement from "routes/merchantManagement";
import transactions from "routes/transactions";

import responseCodeMapping from "utils/response_code_mapping.json";
import EmployeeRoleOptions from "utils/AgentStatusOptions/EmployeeRoleOptions";

import ChangePasswordDialog from "../../components/change-password/ChangePasswordDialog";

const PAGE_LOCALE_KEY = "page.employees.dataTable.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const INPUT_FIELD_INIT_STATE = {
  employeeId: "",
  email: "",
  isoAgentId: "",
  merchantId: "",
  status: "",
  name: "",
  role: "",
};

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  status: "",
  email: "",
  isoAgentId: "",
  merchantId: "",
  name: "",
  role: "",
};

const ERROR_INIT_STATE = {
  employeeId: false,
  email: false,
  isoAgentId: false,
  merchantId: false,
  status: false,
  name: false,
  newPassword: false,
};

const CHANGEPASSWORD_INPUT_INIT_STATE = {
  id: "",
  newPassword: "",
};

const OfflineEmployees = ({ redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "OfflineEmployeeListGrid";

  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE,
  );

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [columnDisplayDialog, setColumnDisplayDialog] = useState({
    open: false,
    position: 0,
  });
  const [columnsArray, setColumnsArray] = useState([]);

  useEffect(() => {
    if (searchFieldInput.id !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.id === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_INIT_STATE);
    }
  }, [searchFieldInput.id]);

  const [changePasswordDialogData, setChangePasswordDialogData] = useState(
    CHANGEPASSWORD_INPUT_INIT_STATE,
  );
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);

  const [queryEmployeeLoading, setQueryEmployeeLoading] = useState(true);
  const [fetchRemarkLoading, setFetchRemarkLoading] = useState(true);

  const isLoading = queryEmployeeLoading || fetchRemarkLoading;

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchRemarks();
    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setQueryEmployeeLoading(true);
    const apiResponse = await queryEmployee(
      trimObject({
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setQueryEmployeeLoading(false);
  };

  const initialFetchRemarks = async () => {
    setFetchRemarkLoading(true);
    const apiResponse = await getSuspensionRemarks();

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
    setFetchRemarkLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      let isSearchValid = true;
      if (searchFieldInput.startDate && !searchFieldInput.endDate) {
        setError((error) => ({ ...error, endDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.endDate && !searchFieldInput.startDate) {
        setError((error) => ({ ...error, startDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.startDate > searchFieldInput.endDate) {
        setError((error) => ({ ...error, invalidDateRange: true }));
        isSearchValid = false;
      }
      const startDate = moment(searchFieldInput.startDate).startOf("day");
      const endDate = moment(searchFieldInput.endDate).startOf("day");
      if (endDate.diff(startDate, "month", true) > 6) {
        setError((error) => ({ ...error, tooLargeDateRange: true }));
        isSearchValid = false;
      }

      if (!isSearchValid) return;
      const allValid = isAllInputValid(error.email);
      if (!allValid) {
        return;
      }
      resetError();
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    initialFetchRemarks();
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryEmployee(payload);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 10,
              data: [],
            });
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };
  // ===================== search field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "employeeId" && searchFieldInput.employeeId !== "")
      return true;
  };

  // ===================== search field function end ===================== //

  // ===================== column display function ===================== //

  const openColumnDisplay = (e) => {
    const position = e.target.getBoundingClientRect().top;
    setColumnDisplayDialog({
      open: true,
      position: position,
    });
    return;
  };

  const closeColumnDisplay = () => {
    setColumnDisplayDialog({
      ...columnDisplayDialog,
      open: false,
    });
    return;
  };

  // ===================== column display function ===================== //
  // ===================== ChangePassword dialog function ===================== //

  const openChangePasswordDialog = (data) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      id: data.email,
    });
    setIsChangePasswordDialogOpen(true);
  };

  const closeChangePasswordDialog = () => {
    restoreChangePasswordDialog();
    setIsChangePasswordDialogOpen(false);
  };

  const restoreChangePasswordDialog = () => {
    setChangePasswordDialogData(CHANGEPASSWORD_INPUT_INIT_STATE);
  };

  const updateChangePasswordPayload = (fieldInput) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      newPassword: fieldInput.newPassword,
    });
  };

  const updateChangePasswordResultCallback = async () => {
    if (changePasswordDialogData.newPassword == "") {
      return;
    }
    setSubmitting(true);

    const payload = {
      adminEmail: changePasswordDialogData.id,
      email: changePasswordDialogData.id,
      newPassword: sha256(changePasswordDialogData.newPassword).toString(),
    };

    const apiResponse = await changePassword(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(DIALOG_LOCALE_KEY + "changePassword.msgFailed") +
                responseCodeMapping[responseBody.responseCode ?? "error"]["en"],
              "error",
            );
          } else {
            snackbar(
              t(DIALOG_LOCALE_KEY + "changePassword.msgSuccess"),
              "success",
            );
          }
        }
      }
    }
    setSubmitting(false);
    closeChangePasswordDialog();
    handleSearch();
  };

  const ChangePasswordDialogContent = () => {
    return (
      <ChangePasswordDialog
        userId={changePasswordDialogData.email}
        updatePayload={updateChangePasswordPayload}
      />
    );
  };

  // ===================== changPassword dialog function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const navigateToDetail = (data) => {
    const path = "/merchant-management/employees/offline-employee-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Employees")
          .page.find((page) => page.path === path).name + data.employeeId,
      path: path,
      state: {
        employeeId: data.employeeId,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const columns = [
    {
      key: "employeeId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.employeeId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "email",
      title: t(PAGE_LOCALE_KEY + "columnTitle.loginName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "merchantId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchantId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "role",
      title: t(PAGE_LOCALE_KEY + "columnTitle.role"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "createdDate",
      title: t(PAGE_LOCALE_KEY + "columnTitle.createdDate"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },

    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS
              variant="text-only"
              className={s.actionBtn}
              onClick={() => {
                return openChangePasswordDialog(data);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.changePassword")}
            </ButtonMAS>

            <ButtonMAS
              variant="text-only"
              className={s.actionBtn}
              onClick={() => {
                const path = "/transactions/offline-payments";
                const payload = {
                  name: transactions.find((tab) => tab.path === path).tabName,
                  path: path,
                  state: {
                    employeeId: data.employeeId,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.actionLabel.payment")}
            </ButtonMAS>
          </div>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.employeeId.label")}
            value={searchFieldInput.employeeId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "employeeId")}
            onfocusFn={() => textInputOnFocus("employeeId")}
            onblurFn={() => textInputOnBlur("employeeId")}
            sxParams={{ textFocus: focus.employeeId }}
            disabled={isInputDisabled("employeeId")}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.loginName.label")}
            value={searchFieldInput.email}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "email")}
            onfocusFn={() => textInputOnFocus("email")}
            onblurFn={() => textInputOnBlur("email")}
            sxParams={{ textFocus: focus.email }}
            disabled={isInputDisabled("email")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.isoAgentId.label")}
            value={searchFieldInput.isoAgentId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "isoAgentId")}
            onfocusFn={() => textInputOnFocus("isoAgentId")}
            onblurFn={() => textInputOnBlur("isoAgentId")}
            sxParams={{ textFocus: focus.isoAgentId }}
            disabled={isInputDisabled("isoAgentId")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          />

          <Selection
            label={t(PAGE_LOCALE_KEY + "search.role.label")}
            selectionValue={searchFieldInput.role}
            onchangeFn={(e) => textInputOnChange(e, "role")}
            onfocusFn={() => textInputOnFocus("role")}
            onblurFn={() => textInputOnBlur("role")}
            options={EmployeeRoleOptions("all")}
            sxParams={{ textFocus: focus.status, textError: error.status }}
            errorMessage={t(PAGE_LOCALE_KEY + "detailColumns.role.errorInput")}
            disabled={isInputDisabled("role")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={isLoading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={isLoading}
          filterStatus={columnDisplayDialog}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          handleColumnDisplay={openColumnDisplay}
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "offline-employees",
          }}
        />
        <div
          className={[s.datatableContainer, isLoading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {isLoading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          <ColumnFilter
            columnDisplayDialog={columnDisplayDialog}
            dialogOncloseFn={closeColumnDisplay}
            columns={columns}
            columnsArray={columnsArray}
            rememberPreference="offline-employees"
            setColumnsArray={setColumnsArray}
          />
          {searching && <Spin type={"search"} />}

          <SimpleDialog
            open={isChangePasswordDialogOpen}
            dialogOncloseFn={closeChangePasswordDialog}
            title={t(DIALOG_LOCALE_KEY + "changePassword.title")}
            content={ChangePasswordDialogContent()}
            positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
            negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
            confirmFn={
              !submitting ? updateChangePasswordResultCallback : () => {}
            }
          />
        </div>
      </div>
    </div>
  );
};
export default React.memo(OfflineEmployees);
